import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Intl from '@fx-ui/intl';
import { ConfigProvider } from '@fx-ui/fine-design';
import { useStores } from '~/hooks';
import { socket } from '~/socket';
import {
    configGlobalTheme,
    getSiteRouter,
    isWxWorkUA,
    logger,
    notSupportTimezone,
    redirect,
    switchLanguage
} from '~/utils';
import { PageContext, RegionContext } from '~/contexts';
import { FALLBACK_REGION, SITE_ROUTER } from '~/constants';
import IntercomProvider from '../intercom-provider';
import { setTags } from '@sentry/browser';
import { jsSdkStore } from '~/models';

interface IAuthProviderProps {
    /**
     * 是否支持站点消息初始化
     * TODO @liaw 在历史相关设计中，原先是将 iframe 中的 websocket 一律屏蔽了，
     *            现在为了解决 g-6891941021，目前仅屏蔽消息中心（iframe）里的websocket初始化
     * @see 历史相关设计 https://fanruan-x.feishu.cn/wiki/EPwOw3R4CiuxGAkE1GbcrdTqnUh#share-D6ridUh5OoIYJbxzTjBc3sOXnxT 4.1.9
     */
    supportSiteMsg?: boolean;
}

/**
 * 初始化成员信息 + initJssdk + socketMsg
 *
 * @param root0
 * @param root0.children
 */
const AuthProvider: React.FC<PropsWithChildren<IAuthProviderProps>> = ({
    children,
    supportSiteMsg = true
}) => {
    const [isCorpUserInit, setCorpUserInit] = useState(false);
    const { initLocale, initDayjs } = useContext(PageContext);
    const { corpUserStore } = useStores();

    useEffect(() => {
        corpUserStore.init().then(() => {
            logger.setBizContext({
                username: corpUserStore.username
            });
            logger.info('user logged in');
            if (notSupportTimezone(corpUserStore.timezone)) {
                redirect(getSiteRouter(SITE_ROUTER.TZ_SUPPORT));
                return;
            }
            configGlobalTheme({
                themeEnabled: corpUserStore.corpThemeEnabled,
                themeColor: corpUserStore.corpThemeColor
            });
            const region = corpUserStore.region || FALLBACK_REGION;
            if (Intl.getLocale() !== corpUserStore.userLocale) {
                switchLanguage(corpUserStore.userLocale);
                initLocale(region);
            } else {
                initDayjs(region);
            }
            setTags({
                userid: corpUserStore.userId,
                nickname: corpUserStore.memberNickname
            });
            if (corpUserStore.hasISVEncryptedIntegration && !isWxWorkUA()) {
                jsSdkStore.initWWOpenData();
            }
            setCorpUserInit(true);
        });
        // 初始化页面消息
        if (supportSiteMsg) {
            socket.initSiteMsg();
        }
    }, []);

    if (!isCorpUserInit) {
        return null;
    }

    return (
        <IntercomProvider>
            <RegionContext.Provider value={corpUserStore.region || FALLBACK_REGION}>
                <ConfigProvider timezone={corpUserStore.timezone}>{children}</ConfigProvider>
            </RegionContext.Provider>
        </IntercomProvider>
    );
};

export default observer(AuthProvider);
